import { useCallback, useEffect, useMemo, useState } from 'react';
import { SubmissionMode } from '@weave/schema-gen-ts/dist/schemas/forms-digital/weave_digital_forms.pb';
import { Template } from '@weave/schema-gen-ts/dist/schemas/messaging/templator/v2/model.pb';
import { ContactType_Enum } from '@weave/schema-gen-ts/dist/shared/persons/v3/enums.pb';
import { DigitalFormsProp } from '@wetags/digital-forms';
import { AppointmentsApiQueries, AppointmentsApiTypes } from '@frontend/api-appointments';
import { DigitalFormsMutations } from '@frontend/api-digital-forms';
import { PersonQueries, PersonsV3 } from '@frontend/api-person';
import { AppointmentSelectorModalContentsV2 } from '@frontend/appointment-selector';
import { withFormsFreemium } from '@frontend/digital-forms-scope';
import { DigitalFormsSendingExperience, FormsSendingExperienceTypes } from '@frontend/digital-forms-sending-experience';
import {
  Category,
  FormPacketSelectorModalContents,
  useFormPacketSelector,
  type FormOrPacketV2,
} from '@frontend/form-packet-selector';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { ModalControlWithKeepState } from '@frontend/integrated-messaging';
import { breakpoints, useMatchMedia } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import { Button, ContentLoader, Modal, ModalControlResponse, useModalControl } from '@frontend/design-system';
import { useTemplateMessagingStore } from '../stores/template-selector-store';
import { TemplateSelectorModal } from './template-selector-modal';

// Stable no-op triggerProps — not used in embedded/messaging mode (no trigger button),
// but required by the ModalControlResponse type contract.
const NOOP_TRIGGER_PROPS: ModalControlResponse['triggerProps'] = {
  onClick: () => {},
  ref: { current: null },
};

type AppointmentSelection = AppointmentsApiTypes.ListAppointmentsIO['output']['appointments'][number];
type InsertArgs = {
  appointmentId?: string;
  appointmentAt?: string;
  submissionId: string;
  formOrPacketLink: string;
  template?: Template;
};
type FormsTemplateModalProps = ModalControlWithKeepState & {
  groupId: string;
  personId?: string;
  personPhone: string;
  onInsert: (args: InsertArgs) => void;
};

const FormsTemplateModalComponent = ({
  groupId,
  personId,
  personPhone,
  onInsert,
  show,
  onClose,
}: FormsTemplateModalProps) => {
  const { t } = useTranslation('inbox-templates');
  const modalControl = useModalControl();
  const isLargeMode = useMatchMedia({ minWidth: breakpoints.xsmall.max });
  const { setSelectedMessageTemplateType } = useTemplateMessagingStore('setSelectedMessageTemplateType');

  // Wetag for enhanced forms flow in messaging (location-scoped)
  const { isEnabled: isEnhancedFormsFlowEnabled, isLoading: isEnhancedFormsFlowLoading } =
    DigitalFormsProp.useWetagQuery('messaging-send-forms-enhanced-flow', groupId);

  // Memoize locationIds to prevent new array reference on every render,
  // which would cascade through useFormsEnabledOrgLocations → useStepsConfig → setCurrentStep(steps[0]) reset
  const memoizedLocationIds = useMemo(() => [groupId], [groupId]);

  const embeddedModalControl = useMemo(
    () => ({
      openModal: modalControl.openModal,
      closeModal: onClose,
      modalProps: { onClose: onClose, show: modalControl.modalProps.show },
      triggerProps: NOOP_TRIGGER_PROPS,
    }),
    [modalControl.openModal, onClose, modalControl.modalProps.show]
  );
  const personQuery = PersonsV3.PersonQueries.useGetPersonLegacyQuery(
    {
      personId: personId ?? '',
      locationIds: [groupId],
    },
    {
      enabled: !!personId,
    }
  );
  const appointmentsQuery = AppointmentsApiQueries.useListPersonAppointments(
    {
      locationId: groupId,
      personId: personId ?? '',
    },
    {
      enabled: !!personId,
      placeholderData: {
        appointments: [],
      },
      retry: 1,
    }
  );
  const householdQuery = PersonQueries.useGetPersonHouseholds(personQuery.data?.householdId ?? '', {
    disabled: !personId || !personQuery.data?.householdId,
    locationId: groupId,
  });
  const allowAppointmentSelection =
    !!personId && (appointmentsQuery.isFetched ? !!appointmentsQuery.data?.appointments.length : true);
  const [currentStep, setCurrentStep] = useState<'appointment' | 'forms' | 'template'>('forms');
  const [selectedAppointment, setSelectedAppointment] = useState<AppointmentSelection>();
  const [selectedFormOrPacket, setSelectedFormOrPacket] = useState<FormOrPacketV2>();
  const {
    selectorProps,
    selectedItem,
    selectedCategory,
    reset: resetFormPacketList,
  } = useFormPacketSelector({
    groupId,
  });

  const handleAppointmentSelection = (appt: AppointmentSelection) => {
    setSelectedAppointment(appt);
    handleInsert(selectedItem, undefined, appt);
  };

  const handleFormOrPacketSelection = (formOrPacket: FormOrPacketV2, selectTemplate?: boolean) => {
    setSelectedFormOrPacket(formOrPacket);
    if (selectTemplate) {
      setCurrentStep('template');
      return;
    }

    handleInsert(formOrPacket);
  };

  const { isLoading, mutateAsync } = DigitalFormsMutations.useCreateFormLinks();

  const handleInsert = async (
    formOrPacket: FormOrPacketV2 | undefined = selectedFormOrPacket,
    template?: Template,
    customAppointment?: AppointmentSelection
  ) => {
    if (!formOrPacket) return;
    const appointment = selectedAppointment || customAppointment;
    const linkRes = await mutateAsync({
      companyId: formOrPacket.companyId || groupId,
      patients: personId
        ? [
            {
              id: personId,
              firstName: personQuery.data?.preferredName || personQuery.data?.firstName,
              lastName: personQuery.data?.lastName,
              birthdate: personQuery.data?.birthdate,
              email: personQuery.data?.contactInfo?.find((c) => c.type === ContactType_Enum.EMAIL)?.destination,
              appointmentId: appointment?.appointmentId,
              phoneNumber: personPhone,
            },
          ]
        : [],
      sendId: formOrPacket.id,
      type: selectedCategory === Category.FORMS ? 'form' : 'packet',
      familyHead: householdQuery.data?.find((h) => h.is_guardian)?.id,
      mode: SubmissionMode.SOLICITED_DIRECT,
    });
    const linkToSend = linkRes.data?.find((link) => !!link.submitUrl);
    if (formOrPacket.id && linkToSend?.submitUrl)
      onInsert({
        appointmentId: appointment?.appointmentId,
        appointmentAt: appointment?.appointmentAt,
        submissionId: linkToSend.token ?? '',
        formOrPacketLink: linkToSend.submitUrl,
        template,
      });

    onClose();
  };

  useEffect(() => {
    if (currentStep === 'appointment' && !allowAppointmentSelection) {
      setCurrentStep('forms');
    }
  }, [allowAppointmentSelection]);

  useEffect(() => {
    if (show === false) {
      setSelectedAppointment(undefined);
      setSelectedFormOrPacket(undefined);
      setCurrentStep(allowAppointmentSelection ? 'appointment' : 'forms');
      resetFormPacketList();
    }
  }, [show]);
  const booleanShow = show === 'hidden' ? false : show;

  // Memoize the onComplete callback to prevent new function reference on every render,
  // which would cascade through useSendFormsProviders useMemo → context re-render → useStepsConfig re-fire.
  const handleSendFormsComplete = useCallback(
    (data: FormsSendingExperienceTypes.FormLinkGeneratedData) => {
      onInsert({
        formOrPacketLink: data.formLink,
        submissionId: data.submissionId,
        appointmentId: data.appointmentId,
        appointmentAt: data.appointmentAt,
      });
      onClose();
    },
    [onInsert, onClose]
  );

  if (isEnhancedFormsFlowLoading) {
    return;
  }

  // Enhanced flow - Use embedded Forms send experience when wetag is enabled
  if (isEnhancedFormsFlowEnabled && personId) {
    return (
      <TemplateSelectorModal {...modalControl.modalProps} show={booleanShow}>
        <DigitalFormsSendingExperience
          origin={FormsSendingExperienceTypes.SendFormsOrigin.MESSAGING}
          personId={personId}
          locationIds={memoizedLocationIds}
          sendFormsModalControls={embeddedModalControl}
          mode='embedded'
          onComplete={handleSendFormsComplete}
        />
      </TemplateSelectorModal>
    );
  }

  // Fallback: Old flow (when wetag is disabled or no personId)
  return (
    <TemplateSelectorModal {...modalControl.modalProps} show={booleanShow}>
      <ContentLoader show={isLoading} />
      {currentStep === 'appointment' && (
        <AppointmentSelectorModalContentsV2
          groupId={groupId}
          personId={personId!}
          onSelect={handleAppointmentSelection}
          onSkip={() => setCurrentStep('forms')}
          onClose={onClose}
          onBack={() => setCurrentStep('forms')}
        />
      )}
      {currentStep === 'forms' && (
        <>
          <Modal.Header onClose={onClose}>
            <Button
              aria-label='close'
              onClick={() => setSelectedMessageTemplateType()}
              iconName='back-small'
              variant='secondary'
            />
            {t('Select Packet or Form')}
          </Modal.Header>
          <FormPacketSelectorModalContents {...selectorProps} />
          <Modal.StickyFooter
            css={isLargeMode && { padding: theme.spacing(4), boxShadow: 'none', justifyContent: 'flex-end' }}
          >
            <div
              css={[
                {
                  display: 'grid',
                  gridTemplateColumns: '2fr 1fr',
                  gap: theme.spacing(1.25),
                },
                isLargeMode && {
                  gridTemplateColumns: 'auto auto',
                },
              ]}
            >
              <Button
                variant='secondary'
                disabled={!selectedItem || !allowAppointmentSelection}
                onClick={() => setCurrentStep('appointment')}
              >
                <Icon name='calendar-add-small' />
                {t('Link To Appointment')}
              </Button>

              <Button disabled={!selectedItem} size='small' onClick={() => handleFormOrPacketSelection(selectedItem!)}>
                {t('Insert')}
              </Button>
            </div>
          </Modal.StickyFooter>
        </>
      )}
    </TemplateSelectorModal>
  );
};

export const FormsTemplateModal = withFormsFreemium(FormsTemplateModalComponent);
